import * as React from 'react';
import moment from 'moment-timezone';
import { Modal } from 'react-bootstrap';

// TypeScript interfaces
export interface Note {
  id: number;
  profile_id: number;
  note: string;
  created_at: string;
}

export interface ProfileNotesProps {
  profile_id: number;
  authenticity_token: string;
  load_on_demand?: boolean;
}

export interface ApplicantsNotesProps {
  profile_id: number;
  notes: Note[];
  authenticity_token: string;
  load_notes: () => void;
  loaded: boolean;
}

export interface AllNotesProps {
  profile_id: number;
  notes: Note[];
  authenticity_token: string;
  loadNotesCallback: () => void;
}

export interface AllNotesModalProps {
  show: boolean;
  onHide: () => void;
  profile: {
    id: number;
    fullname: string;
  };
  notes: Note[];
  authenticity_token: string;
  loadNotesCallback: () => void;
}

export interface NewNoteProps {
  profile_id: number;
  authenticity_token: string;
  loadNotesCallback: () => void;
  closeNotesCallback?: () => void;
}

const truncate = (str: string): string => {
  return str.length > 120 ? str.substring(0, 120) + "..." : str;
}
export const ApplicantsNotes: React.FC<ApplicantsNotesProps> = ({ profile_id, notes, authenticity_token, load_notes, loaded }) => {
  const loadNotesCallback = () => load_notes();

  return (

    <div className="app-content d-flex flex-column justify-content-between px-0">
      <div className="info p-2">
        {notes.length < 1 && !loaded && (
          <div className="text-center">Loading...</div>
        )}
        {notes.length < 1 && loaded && (
          <p>Save a note to remember important details about this candidate. </p>
        )}
        {notes.length > 0 && (
          <div
            className="p-3 rounded"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f4f5f6 100%)',
              border: '1.5px solid #dee2e6',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s ease',
              cursor: 'default',
              position: 'relative'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div className="d-flex align-items-start">
              <i className="fas fa-sticky-note me-2 mt-1" style={{ color: '#085ec8', fontSize: '0.875rem' }}></i>
              <div className="flex-grow-1">
                <small className="d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d' }}>
                  <i className="far fa-clock me-1"></i>
                  {moment(notes[0].created_at).format('MMM D, YYYY [at] h:mm A')}
                </small>
                <div style={{ fontSize: '0.85rem', lineHeight: '1.5', color: '#4a5568' }}>
                  {truncate(notes[0].note)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="p-1 d-flex flex-row" style={{ gap: '0.75rem' }}>
        <AllNotes
          profile_id={profile_id}
          notes={notes}
          authenticity_token={authenticity_token}
          loadNotesCallback={loadNotesCallback}
        />
        <NewNote
          profile_id={profile_id}
          authenticity_token={authenticity_token}
          loadNotesCallback={loadNotesCallback}
        />
      </div>
    </div>

  );
}

const get_notes_api = ({ profile_id, callback }: { profile_id: number; callback: (notes: Note[]) => void }) => {
  if (profile_id) {
    fetch('/profile_notes.json?profile_id=' + profile_id)
      .then(res => res.json())
      .then(res => {
        // An expired session or server error yields valid JSON that is NOT an
        // array (e.g. devise's {"error": ...}); passing it through crashed the
        // notes panel on .filter (Sentry DIRECTDENTAL-24F).
        return callback(Array.isArray(res) ? res : []);
      })
      .catch(() => callback([]))
  } else {
    return callback([]);
  }
}

const ProfileNotes: React.FC<ProfileNotesProps> = ({ profile_id, authenticity_token, load_on_demand = false }) => {
  const [state, setState] = React.useState<{ notes: Note[]; loaded: boolean }>({ notes: [], loaded: false });

  const loadNotesCallback = () => {
    getNotes();
  }

  const getNotes = () => {
    get_notes_api({
      profile_id: profile_id,
      callback: (notes: Note[]) => {
        setState({
          ...state,
          notes: notes.filter(note => note.profile_id === profile_id),
          loaded: true
        })
      }
    });
  }

  React.useEffect(() => {
    if (load_on_demand) getNotes();
  }, []);

  const { notes, loaded } = state;

  return (
    <div>
      {notes.length < 1 && !loaded && (
        <div className="text-center text-muted py-3">
          <i className="fas fa-spinner fa-spin me-2"></i>
          Loading notes...
        </div>
      )}
      {notes.length < 1 && loaded && (
        <div className="text-muted" style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
          <i className="fas fa-info-circle me-2"></i>
          Save a note to remember important details about this candidate.
        </div>
      )}

      {notes.length > 0 && (
        <div
          className="mb-3 p-3 rounded"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f4f5f6 100%)',
            border: '1.5px solid #dee2e6',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.3s ease',
            cursor: 'default',
            position: 'relative'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)';
          }}
        >
          <div className="d-flex align-items-start">
            <i className="fas fa-sticky-note me-2 mt-1" style={{ color: '#085ec8' }}></i>
            <div className="flex-grow-1">
              <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {moment(notes[0].created_at).format('MMM D, YYYY [at] h:mm A')}
              </small>
              <div style={{ fontSize: '0.9rem', lineHeight: '1.5', color: '#4a5568' }}>
                {notes[0].note}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex flex-column mt-2" style={{ gap: '0.75rem' }}>
        <NewNote profile_id={profile_id} authenticity_token={authenticity_token} loadNotesCallback={loadNotesCallback} />
        <AllNotes authenticity_token={authenticity_token} loadNotesCallback={loadNotesCallback} profile_id={profile_id} notes={notes} />
      </div>
    </div>
  )
}

// Standalone All Notes Modal Component (for external control)
export const AllNotesModal: React.FC<AllNotesModalProps> = ({ show, onHide, profile, notes, authenticity_token, loadNotesCallback }) => {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #085ec8, #176fc9)', color: 'white', border: 'none' }}>
        <Modal.Title>
          <i className="fas fa-book me-2"></i> All Notes for {profile.fullname}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '1.5rem' }}>
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          {notes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon">
                <i className="fas fa-sticky-note"></i>
              </div>
              <h4 className="empty-state__title">No Notes Yet</h4>
              <p className="empty-state__description">
                Create your first note to remember important details about this candidate.
              </p>
            </div>
          ) : (
            notes.map((note, index) => (
              <div
                key={index}
                className="mb-3 p-3 rounded"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f4f5f6 100%)',
                  border: '1.5px solid #dee2e6',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(0, 0, 0, 0.05)';
                }}
              >
                <div className="d-flex align-items-start">
                  <i className="fas fa-sticky-note me-2 mt-1" style={{ color: '#085ec8' }}></i>
                  <div className="flex-grow-1">
                    <small className="text-muted d-block mb-2" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      <i className="far fa-clock me-1"></i>
                      {moment(note.created_at).format('MMM D, YYYY [at] h:mm A')}
                    </small>
                    <div style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#4a5568' }}>{note.note}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid #e8ecef' }}>
          <NewNote profile_id={profile.id} authenticity_token={authenticity_token} loadNotesCallback={loadNotesCallback} closeNotesCallback={onHide} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

const AllNotes: React.FC<AllNotesProps> = ({ profile_id, notes, authenticity_token, loadNotesCallback }) => {
  const [modalOpen, setModalOpen] = React.useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  return (
    <>
      <button
        type="button"
        className="w-100"
        onClick={openModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          borderRadius: '0.5rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          background: 'white',
          color: '#085ec8',
          border: '2px solid #085ec8',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 6px rgba(8, 94, 200, 0.12)'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#085ec8';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.2)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.color = '#085ec8';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.12)';
        }}
      >
        <i className="fas fa-book"></i> All Notes
      </button>
      <AllNotesModal
        show={modalOpen}
        onHide={closeModal}
        profile={{ id: profile_id, fullname: '' }}
        notes={notes}
        authenticity_token={authenticity_token}
        loadNotesCallback={loadNotesCallback}
      />
    </>
  )
}

const NewNote: React.FC<NewNoteProps> = ({ profile_id, authenticity_token, loadNotesCallback, closeNotesCallback }) => {
  const [state, setState] = React.useState<{ note: string; submitted: boolean }>({ note: '', submitted: false })

  const [modalOpen, setModalOpen] = React.useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const set_note = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setState({ ...state, note: event.target.value })

  const submit_note = (callback: () => void, close: () => void) => {
    const url = `/profile_notes.json`;

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': authenticity_token
      },
      body: JSON.stringify({
        profile_id,
        note: state.note
      })
    })
      .then(res => res.json())
      .then(res => {
        setState({ ...state, note: '', submitted: true })
        return res;
      })
      .then(res => {
        callback();
        closeModal();
        if (closeNotesCallback) closeNotesCallback();
      })
  }
  return (
    <>
      <button
        type="button"
        className="w-100"
        onClick={openModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          borderRadius: '0.5rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          background: 'white',
          color: '#085ec8',
          border: '2px solid #085ec8',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 6px rgba(8, 94, 200, 0.12)'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#085ec8';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.2)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.color = '#085ec8';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.12)';
        }}
      >
        <i className="fas fa-plus"></i> New Note
      </button>
      <Modal show={modalOpen} onHide={closeModal} centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #085ec8, #176fc9)', color: 'white', border: 'none' }}>
          <Modal.Title>
            <i className="fas fa-plus me-2"></i> New Note
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          <div className="alert alert-info d-flex align-items-center mb-3" style={{ fontSize: '0.9rem', background: 'rgba(8, 94, 200, 0.1)', border: '1px solid rgba(8, 94, 200, 0.2)', color: '#034ba6' }}>
            <i className="fas fa-lock me-2"></i>
            <span>Notes are private and only visible to your team</span>
          </div>

          <div>
            <label className="form-label fw-semibold mb-2" style={{ color: '#034ba6' }}>
              <i className="fas fa-edit me-1"></i> Note Content
            </label>
            <textarea
              value={state.note}
              placeholder="Type your note here..."
              onChange={set_note}
              name="note"
              id="profile_notes[note]"
              rows={8}
              className="form-control mb-3"
              style={{
                fontSize: '0.95rem',
                resize: 'vertical',
                border: '1px solid #e8ecef',
                borderRadius: '0.5rem',
                padding: '0.75rem'
              }}
            />
            <button
              className="btn btn-primary w-100"
              style={{
                background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                border: 'none',
                padding: '0.75rem',
                fontWeight: 600
              }}
              onClick={() => submit_note(loadNotesCallback, closeModal)}
              disabled={!state.note.trim()}
            >
              <i className="fas fa-save me-2"></i>
              Save Note
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ProfileNotes;
